import { Injectable, inject, OnDestroy, DOCUMENT } from '@angular/core';

@Injectable()
export class PollyManageTypingService implements OnDestroy {
  private readonly document = inject(DOCUMENT);
  private frameId: number | null = null;
  private lastTick: number = 0;
  private currentIndex: number = 0;
  private typingInterval: number = 25;
  private fullText: string = '';
  private isActive: boolean = false;
  private visibilityAttached: boolean = false;
  private onUpdate: ((text: string) => void) | null = null;
  private onComplete: VoidFunction | null = null;

  constructor() {
    this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
  }

  public start(fullText: string, onUpdate: (text: string) => void, typingSpeedMsPerChar: number = 25, onComplete?: () => void): void {
    this.stop();
    this.isActive = true;
    this.fullText = fullText;
    this.onUpdate = onUpdate;
    this.onComplete = onComplete ?? null;
    this.currentIndex = 0;
    this.typingInterval = typingSpeedMsPerChar;
    this.lastTick = performance.now();
    this.attachVisibilityListener();

    if (this.document.hidden) {
      this.finishImmediately();
      return;
    }

    const loop = (time: number): void => {
      if (!this.isActive || !this.onUpdate) { return; }
      const elapsed = time - this.lastTick;
      const charsToAdd = Math.floor(elapsed / this.typingInterval);
      if (charsToAdd > 0) {
        this.currentIndex = Math.min(this.currentIndex + charsToAdd, this.fullText.length);
        this.onUpdate(this.fullText.slice(0, this.currentIndex));
        this.lastTick += charsToAdd * this.typingInterval;
      }

      if (this.currentIndex < this.fullText.length && this.isActive) {
        this.frameId = requestAnimationFrame(loop);
      } else {
        this.complete();
      }
    };

    this.frameId = requestAnimationFrame(loop);
  }

  public stop(): void {
    this.isActive = false;
    if (this.frameId !== null) {
      cancelAnimationFrame(this.frameId);
      this.frameId = null;
    }
    this.detachVisibilityListener();
    this.reset();
  }

  public destroy(): void {
    this.stop();
  }

  public ngOnDestroy(): void {
    this.destroy();
  }

  private attachVisibilityListener(): void {
    if (this.visibilityAttached) { return; }
    this.document.addEventListener('visibilitychange', this.handleVisibilityChange);
    this.visibilityAttached = true;
  }

  private detachVisibilityListener(): void {
    if (!this.visibilityAttached) { return; }
    this.document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    this.visibilityAttached = false;
  }

  private handleVisibilityChange(): void {
    if (this.document.hidden && this.fullText && this.onUpdate && this.isActive) {
      this.finishImmediately();
    }
  }

  private finishImmediately(): void {
    if (!this.isActive) { return; }
    const update = this.onUpdate;
    const text = this.fullText;
    const complete = this.onComplete;
    this.stop();
    if (update) { update(text); }
    if (complete) { complete(); }
  }

  private complete(): void {
    const complete = this.onComplete;
    this.reset();
    if (complete) { complete(); }
  }

  private reset(): void {
    this.fullText = '';
    this.currentIndex = 0;
    this.lastTick = 0;
    this.onUpdate = null;
    this.onComplete = null;
  }
}